import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Waveform, Gauge, Timer, Function, ArrowClockwise, ChartLine, ChartBar,
    ChartPieSlice, Broadcast, Lightbulb, CheckCircle, WarningCircle, Warning,
    Target,
} from "@phosphor-icons/react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine, ScatterChart, Scatter, BarChart, Bar, Cell,
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════════
 * AnalyticsSpc — Six-Sigma / SPC dashboard for a single operation.
 * User picks: op-type (vessel/tronox_export/machine/conveyor) → instance
 *   (or "All"), metric, and a timeframe preset. Backend returns descriptive
 *   stats + histogram + Q-Q + box + I-chart + MTBF/MTTR/Avail/Util/Reliab.
 * ═══════════════════════════════════════════════════════════════════════ */

const OP_TYPE_META = {
    vessel:         { label: "Vessels",              icon: "🚢", singular: "vessel" },
    tronox_export:  { label: "Tronox exports",       icon: "🏗️", singular: "export" },
    tronox_inbound: { label: "Tronox inbound",       icon: "📥", singular: "silo" },
    machine:        { label: "Fleet machines",       icon: "🚜", singular: "machine" },
    conveyor:       { label: "Conveyors",            icon: "⚙️", singular: "conveyor" },
};

const PRESETS = [
    { k: "today",  label: "Today",     hours: 24 },
    { k: "24h",    label: "24 hours",  hours: 24 },
    { k: "7d",     label: "7 days",    hours: 24 * 7 },
    { k: "30d",    label: "30 days",   hours: 24 * 30 },
    { k: "90d",    label: "90 days",   hours: 24 * 90 },
    { k: "custom", label: "Custom",    hours: null },
];

function isoStartOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}
function isoNow() { return new Date().toISOString(); }
function isoHoursAgo(h) { return new Date(Date.now() - h * 3600 * 1000).toISOString(); }

function localDT(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    // yyyy-MM-ddTHH:mm for <input type="datetime-local">
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalDT(s) { return s ? new Date(s).toISOString() : null; }

export default function AnalyticsSpc() {
    const [ops, setOps] = useState(null);
    const [opType, setOpType] = useState("vessel");
    const [opId, setOpId] = useState("all");
    const [metric, setMetric] = useState("flow_rate_tph");
    const [preset, setPreset] = useState("30d");
    const [start, setStart] = useState(isoHoursAgo(24 * 30));
    const [end, setEnd] = useState(isoNow());
    const [period, setPeriod] = useState("auto");
    const [usl, setUsl] = useState("");
    const [lsl, setLsl] = useState("");
    const [target, setTarget] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Load ops catalogue once
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/analytics/operations");
                setOps(r.data);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, []);

    // Recompute start/end when preset changes
    useEffect(() => {
        if (preset === "custom") return;
        const now = new Date();
        let s;
        if (preset === "today") {
            const t = new Date(); t.setHours(0, 0, 0, 0);
            s = t.toISOString();
        } else {
            const p = PRESETS.find((x) => x.k === preset);
            s = isoHoursAgo(p.hours);
        }
        setStart(s);
        setEnd(now.toISOString());
    }, [preset]);

    // Available metrics for the current op-type
    const availableMetrics = useMemo(() => {
        if (!ops?.metrics) return [];
        return Object.entries(ops.metrics)
            .filter(([_, m]) => (m.applies || []).includes(opType))
            .map(([k, m]) => ({ key: k, ...m }));
    }, [ops, opType]);

    // Keep metric compatible when op-type changes
    useEffect(() => {
        if (!availableMetrics.length) return;
        if (!availableMetrics.find((m) => m.key === metric)) {
            setMetric(availableMetrics[0].key);
        }
    }, [availableMetrics]);   // eslint-disable-line

    // Instance list for the current op-type
    const instances = useMemo(() => {
        if (!ops) return [];
        const map = {
            vessel: "vessels",
            tronox_export: "tronox_exports",
            tronox_inbound: "tronox_inbounds",
            machine: "machines",
            conveyor: "conveyors",
        };
        return ops[map[opType]] || [];
    }, [ops, opType]);

    // Reset op_id when op-type changes
    useEffect(() => { setOpId("all"); }, [opType]);

    const runAnalysis = async () => {
        setLoading(true);
        try {
            const params = {
                op_type: opType, op_id: opId, metric,
                start, end,
                period,
            };
            if (usl !== "" && !Number.isNaN(parseFloat(usl))) params.usl = parseFloat(usl);
            if (lsl !== "" && !Number.isNaN(parseFloat(lsl))) params.lsl = parseFloat(lsl);
            if (target !== "" && !Number.isNaN(parseFloat(target))) params.target = parseFloat(target);
            const r = await api.get("/analytics/spc", { params });
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    // Auto-run whenever selectors change — gated on metric compatibility to
    // avoid a stale-metric transient 400 while the compat useEffect swaps it.
    useEffect(() => {
        if (!ops) return;
        if (!availableMetrics.length) return;
        if (!availableMetrics.some((m) => m.key === metric)) return;
        runAnalysis();
        // eslint-disable-next-line
    }, [ops, opType, opId, metric, start, end, availableMetrics, usl, lsl, target, period]);

    return (
        <div className="g-card mt-8" data-testid="spc-analytics">
            <div className="g-card-header-dark">
                <Waveform size={18} />
                <div className="font-display font-bold uppercase tracking-wide">SPC · Six-Sigma per Operation</div>
            </div>
            <div className="p-5 space-y-5">
                {/* ── Selectors ────────────────────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <Selector label="Operation type" testid="spc-op-type" value={opType} onChange={setOpType}
                        options={Object.entries(OP_TYPE_META).map(([k, m]) => ({ value: k, label: `${m.icon} ${m.label}` }))} />
                    <Selector label="Operation" testid="spc-op-id" value={opId} onChange={setOpId}
                        options={[{ value: "all", label: `All ${OP_TYPE_META[opType].label.toLowerCase()}` },
                                  ...instances.map((it) => ({ value: it.id, label: it.label, sub: it.sublabel }))]} />
                    <Selector label="Metric" testid="spc-metric" value={metric} onChange={setMetric}
                        options={availableMetrics.map((m) => ({ value: m.key, label: `${m.label} (${m.unit})` }))} />
                    <Selector label="Timeframe" testid="spc-preset" value={preset} onChange={setPreset}
                        options={PRESETS.map((p) => ({ value: p.k, label: p.label }))} />
                    <Selector label="Bucket period" testid="spc-period" value={period} onChange={setPeriod}
                        options={[
                            { value: "auto",   label: "Auto (smart)" },
                            { value: "hourly", label: "Hourly" },
                            { value: "shift",  label: "Shift (12h)" },
                            { value: "daily",  label: "Daily" },
                            { value: "weekly", label: "Weekly" },
                        ]} />
                </div>
                {preset === "custom" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 -mt-1">
                        <label className="text-xs">
                            <div className="uppercase-label mb-1 text-[10px]">Start</div>
                            <input type="datetime-local" className="g-input w-full" value={localDT(start)}
                                onChange={(e) => setStart(fromLocalDT(e.target.value))} data-testid="spc-custom-start" />
                        </label>
                        <label className="text-xs">
                            <div className="uppercase-label mb-1 text-[10px]">End</div>
                            <input type="datetime-local" className="g-input w-full" value={localDT(end)}
                                onChange={(e) => setEnd(fromLocalDT(e.target.value))} data-testid="spc-custom-end" />
                        </label>
                        <div className="flex items-end">
                            <button onClick={runAnalysis} className="g-btn g-btn-primary w-full inline-flex items-center justify-center gap-1" data-testid="spc-run">
                                <ArrowClockwise size={14} /> Refresh
                            </button>
                        </div>
                    </div>
                )}

                {/* Spec-limits row — optional Cp/Cpk / DPMO inputs */}
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center gap-1 mb-2">
                        <Target size={13} className="text-slate-500" />
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                            Process spec limits <span className="text-slate-400 lowercase italic">(optional — leave blank for auto-derived ±3σ)</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <label className="text-xs">
                            <div className="uppercase-label mb-1 text-[10px]">Lower spec limit (LSL)</div>
                            <input type="number" step="any" className="g-input w-full" value={lsl}
                                placeholder={data?.capability?.auto_spec ? `auto ${data.capability.lsl}` : "e.g. 100"}
                                onChange={(e) => setLsl(e.target.value)} data-testid="spc-lsl" />
                        </label>
                        <label className="text-xs">
                            <div className="uppercase-label mb-1 text-[10px]">Target</div>
                            <input type="number" step="any" className="g-input w-full" value={target}
                                placeholder="e.g. 500"
                                onChange={(e) => setTarget(e.target.value)} data-testid="spc-target" />
                        </label>
                        <label className="text-xs">
                            <div className="uppercase-label mb-1 text-[10px]">Upper spec limit (USL)</div>
                            <input type="number" step="any" className="g-input w-full" value={usl}
                                placeholder={data?.capability?.auto_spec ? `auto ${data.capability.usl}` : "e.g. 800"}
                                onChange={(e) => setUsl(e.target.value)} data-testid="spc-usl" />
                        </label>
                    </div>
                </div>

                {/* ── Header / status ──────────────────────────────── */}
                {loading ? (
                    <div className="text-slate-500 text-sm text-center py-8" data-testid="spc-loading">Crunching numbers…</div>
                ) : !data ? (
                    <div className="text-slate-400 text-sm text-center py-8">Pick selectors above to begin.</div>
                ) : (
                    <>
                        <div className="border-t border-slate-200 pt-4">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Analysing</div>
                            <div className="font-display font-black text-xl text-[var(--grindrod-navy)]">{data.op_label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                {data.metric_label} · <span className="tabular">{data.stats.n}</span> data points · window {new Date(data.start).toLocaleString()} → {new Date(data.end).toLocaleString()}
                                {data.is_reliability_metric && data.period_hours != null && (
                                    <> · bucket <b>{fmtBucket(data.period_hours)}</b></>
                                )}
                            </div>
                        </div>

                        {/* ── KPI tiles ─────────────────────────────── */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="spc-kpis">
                            <KpiTile icon={<Timer size={16} />} label="MTBF" value={fmtHours(data.kpis.mtbf_hours)} sub="Mean time BETWEEN failures" />
                            <KpiTile icon={<ArrowClockwise size={16} />} label="MTTR" value={fmtHours(data.kpis.mttr_hours)} sub="Mean time to repair" />
                            <KpiTile icon={<Gauge size={16} />} label="Availability" value={pct(data.kpis.availability_pct)} sub={`${fmtHours(data.kpis.uptime_hours)} uptime`} tone={toneForPct(data.kpis.availability_pct)} />
                            <KpiTile icon={<ChartLine size={16} />} label="Utilisation" value={pct(data.kpis.utilisation_pct)} sub={`${fmtHours(data.kpis.active_hours)} active`} tone={toneForPct(data.kpis.utilisation_pct)} />
                            <KpiTile icon={<Broadcast size={16} />} label="Reliability" value={pct(data.kpis.reliability_pct)} sub={`${data.kpis.num_failures} failure${data.kpis.num_failures === 1 ? "" : "s"}`} tone={toneForPct(data.kpis.reliability_pct)} />
                            <KpiTile icon={<Target size={16} />} label="Sigma Level"
                                value={data.capability.sigma_level != null ? `${data.capability.sigma_level}σ` : "—"}
                                sub={data.capability.auto_spec
                                    ? "auto-spec — set USL/LSL for real σ level"
                                    : (data.capability.dpmo != null ? `${Math.round(data.capability.dpmo).toLocaleString()} DPMO` : "no spec")}
                                tone={data.capability.auto_spec ? "text-slate-500" : toneForSigma(data.capability.sigma_level)} />
                        </div>

                        {/* Capability strip (Cp / Cpk / Pp / Ppk) */}
                        <CapabilityStrip cap={data.capability} unit={data.metric_unit} />

                        {/* ── Descriptive stats ──────────────────── */}
                        <DescriptivePanel stats={data.stats} normality={data.normality} unit={data.metric_unit} />

                        {data.stats.n < 3 ? (
                            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="spc-empty">
                                <div className="text-slate-700 font-bold">Not enough data yet</div>
                                <div className="text-slate-500 text-sm mt-1">This operation has fewer than 3 data points in the window — widen the timeframe or pick a different operation.</div>
                            </div>
                        ) : (
                            <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Stability run chart (I-chart) with Nelson violation highlights */}
                                <ChartCard title="Stability run chart (I-chart)" icon={<ChartLine size={16} />} testid="spc-run-chart"
                                    badge={data.nelson.in_control
                                        ? { text: "IN CONTROL", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" }
                                        : { text: `${data.nelson.total_violations} NELSON VIOLATION${data.nelson.total_violations === 1 ? "" : "S"}`, tone: "bg-rose-100 text-rose-700 border-rose-200" }}>
                                    <RunChart data={data.series} control={data.control}
                                        median={data.stats.median} unit={data.metric_unit}
                                        flagged={new Set(data.nelson.unique_points_flagged)} />
                                    <Legend items={[
                                        { color: "#0F1E37", label: "Value" },
                                        { color: "#DC2626", label: "Nelson violation" },
                                        { color: "#059669", label: `CL ${data.control.cl}` },
                                        { color: "#EA580C", label: `Median ${data.stats.median}` },
                                        { color: "#DC2626", label: `UCL/LCL (±3σ)` },
                                    ]} />
                                    <RunsTestPanel runs={data.runs_test} />
                                </ChartCard>

                                {/* Normal probability (Q-Q) plot */}
                                <ChartCard title="Normal probability plot (Q-Q)" icon={<Function size={16} />} testid="spc-qq-chart"
                                    badge={data.normality.verdict === "normal"
                                        ? { text: "NORMAL (AD p≥0.05)", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" }
                                        : data.normality.verdict === "non_normal"
                                        ? { text: `NON-NORMAL (AD ${fmtP(data.normality.p_value)})`, tone: "bg-amber-100 text-amber-700 border-amber-200" }
                                        : null}>
                                    <QqChart data={data.qq} unit={data.metric_unit} />
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        Anderson-Darling A² = <b>{data.normality.a2_adj ?? "—"}</b>,
                                        p-value <b>{fmtP(data.normality.p_value)}</b>.
                                        Points close to the reference line → data is approximately normal.
                                    </div>
                                </ChartCard>

                                {/* Outliers box plot */}
                                <ChartCard title="Outliers · box plot" icon={<ChartBar size={16} />} testid="spc-box-chart"
                                    badge={{
                                        text: `${data.boxplot.outliers.length} OUTLIER${data.boxplot.outliers.length === 1 ? "" : "S"}`,
                                        tone: data.boxplot.outliers.length === 0
                                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                            : (data.boxplot.outliers.length / data.stats.n) > 0.05
                                                ? "bg-rose-100 text-rose-700 border-rose-200"
                                                : "bg-amber-100 text-amber-700 border-amber-200"
                                    }}>
                                    <BoxChart box={{ ...data.boxplot, n: data.stats.n }} unit={data.metric_unit} />
                                    <div className="text-[10px] text-slate-500 mt-1 text-center">
                                        Vertical box plot · whiskers at Q1−1.5·IQR / Q3+1.5·IQR · red circles = outliers ({data.boxplot.outliers.length} of {data.stats.n}).
                                    </div>
                                </ChartCard>

                                {/* Centring histogram + normal + spec limits */}
                                <ChartCard title="Centring histogram + normal curve" icon={<ChartPieSlice size={16} />} testid="spc-hist-chart"
                                    badge={data.capability.cpk != null
                                        ? { text: data.capability.auto_spec
                                            ? `Cpk ${data.capability.cpk} · AUTO`
                                            : `Cpk ${data.capability.cpk}`,
                                            tone: data.capability.auto_spec
                                                ? "bg-slate-100 text-slate-600 border-slate-200"
                                                : toneForCpk(data.capability.cpk) }
                                        : null}>
                                    <HistogramChart hist={data.histogram} stats={data.stats} cap={data.capability} unit={data.metric_unit} />
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        Bars = observed counts. Line = normal PDF fit at μ={data.stats.mean}, σ={data.stats.stdev}.
                                        {data.capability.cpk != null && (
                                            <> Cp = <b>{data.capability.cp}</b>, Cpk = <b>{data.capability.cpk}</b>{data.capability.auto_spec && " (auto-derived spec)"}.</>
                                        )}
                                    </div>
                                </ChartCard>
                            </div>

                            {/* Automatic Six-Sigma findings */}
                            <FindingsPanel findings={data.findings} />
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/* ─── Sub-components ────────────────────────────────────────────────── */

function Selector({ label, testid, value, onChange, options }) {
    return (
        <label className="block">
            <div className="uppercase-label text-[10px] mb-1">{label}</div>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="g-input w-full text-sm"
                data-testid={testid}
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

function KpiTile({ icon, label, value, sub, tone = "text-[var(--grindrod-navy)]" }) {
    return (
        <div className="rounded border border-slate-200 bg-white p-3" data-testid={`kpi-${label.toLowerCase()}`}>
            <div className="flex items-center gap-1 text-slate-500">
                {icon}
                <div className="uppercase-label text-[9px]">{label}</div>
            </div>
            <div className={`metric-value tabular text-xl mt-1 ${tone}`}>{value}</div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function ChartCard({ title, icon, testid, badge, children }) {
    return (
        <div className="rounded border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-1">
                {icon}
                <div className="uppercase-label text-[10px] font-bold">{title}</div>
                {badge && (
                    <span className={`ml-auto inline-block px-2 py-0.5 rounded border text-[10px] font-black tracking-wider ${badge.tone}`}
                          data-testid={`${testid}-badge`}>
                        {badge.text}
                    </span>
                )}
            </div>
            <div className="p-3">{children}</div>
        </div>
    );
}

function Legend({ items }) {
    return (
        <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-600">
            {items.map((i, idx) => (
                <span key={idx} className="inline-flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: i.color }} /> {i.label}
                </span>
            ))}
        </div>
    );
}

function DescriptivePanel({ stats, normality, unit }) {
    // SigmaXL-style descriptive table — statistic name / value pairs.
    // P-values <0.05 render in rose to flag non-normality / distribution issues.
    const RED = "text-rose-600 font-black";
    const isFlagged = (p) => (p != null && p < 0.05);
    const pFmt = (p) => (p == null ? "—" : p < 0.001 ? "<0.001" : p);
    return (
        <div className="rounded border border-slate-200 bg-white overflow-hidden" data-testid="spc-descriptive">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                <ChartBar size={14} className="text-slate-500" />
                <div className="uppercase-label text-[10px] font-bold">
                    Descriptive statistics <span className="text-slate-500 normal-case">({unit || ""})</span>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 px-3 py-2 text-[12px]">
                <Row label="Count"                       value={stats.n} />
                <Row label="Mean"                        value={stats.mean} />
                <Row label="StDev"                       value={stats.stdev} />
                <Row label="Range"                       value={stats.range} />
                <Row label="Minimum"                     value={stats.min} />
                <Row label="25th Percentile (Q1)"        value={stats.q1} />
                <Row label="50th Percentile (Median)"    value={stats.median} />
                <Row label="75th Percentile (Q3)"        value={stats.q3} />
                <Row label="Maximum"                     value={stats.max} />
                <Row label="95% CI Mean"                 value={stats.ci_mean_lo != null && stats.ci_mean_hi != null ? `${stats.ci_mean_lo} to ${stats.ci_mean_hi}` : "—"} />
                <Row label="95% CI Sigma"                value={stats.ci_sigma_lo != null && stats.ci_sigma_hi != null ? `${stats.ci_sigma_lo} to ${stats.ci_sigma_hi}` : "—"} />
                <Row label="Anderson-Darling Normality"  value={normality?.a2_adj ?? "—"} />
                <Row label="  P-Value (A-D Test)"        value={pFmt(normality?.p_value)}  valueClass={isFlagged(normality?.p_value) ? RED : ""} raw />
                <Row label="Skewness"                    value={stats.skewness} />
                <Row label="  P-Value (Skewness)"        value={pFmt(stats.p_skewness)}    valueClass={isFlagged(stats.p_skewness) ? RED : ""} raw />
                <Row label="Kurtosis"                    value={stats.kurtosis} />
                <Row label="  P-Value (Kurtosis)"        value={pFmt(stats.p_kurtosis)}    valueClass={isFlagged(stats.p_kurtosis) ? RED : ""} raw />
                <Row label="Coefficient of Variation"    value={stats.cv_pct != null ? `${stats.cv_pct}%` : "—"} />
                <Row label="Interquartile Range (IQR)"   value={stats.iqr} />
            </div>
        </div>
    );
}

function Row({ label, value, valueClass = "", raw = false }) {
    return (
        <>
            <div className={`text-slate-700 ${raw ? "text-[11px] pl-2" : ""}`}>{label}</div>
            <div className={`text-right tabular font-semibold text-[var(--grindrod-navy)] ${valueClass}`}>{value ?? "—"}</div>
        </>
    );
}

const RunChartDot = (p) => (
    <circle key={p.key} cx={p.cx} cy={p.cy} r={p.payload.flagged ? 4.5 : 3}
            fill={p.payload.flagged ? "#DC2626" : "#0F1E37"}
            stroke={p.payload.flagged ? "#DC2626" : "#0F1E37"} />
);

function RunChart({ data, control, median, unit, flagged }) {
    const flags = flagged || new Set();
    const rows = data.map((d, i) => ({ i: i + 1, v: d.v, t: d.t, label: d.label, flagged: flags.has(i) }));
    return (
        <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={rows} margin={{ top: 8, right: 10, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#EEF1F5" />
                    <XAxis dataKey="i" stroke="#64748B" fontSize={10} label={{ value: "sample #", position: "insideBottom", offset: -2, fontSize: 10, fill: "#64748B" }} />
                    <YAxis stroke="#64748B" fontSize={10} label={{ value: unit, angle: -90, position: "insideLeft", fontSize: 10, fill: "#64748B" }} />
                    <Tooltip
                        contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1", fontSize: 11 }}
                        labelFormatter={(v, payload) => (payload && payload[0]) ? `#${v} · ${new Date(payload[0].payload.t).toLocaleString()}${payload[0].payload.flagged ? ' · ⚠ Nelson' : ''}` : `#${v}`}
                    />
                    {control?.ucl != null && <ReferenceLine y={control.ucl} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "UCL", position: "right", fontSize: 10, fill: "#DC2626" }} />}
                    {control?.lcl != null && <ReferenceLine y={control.lcl} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "LCL", position: "right", fontSize: 10, fill: "#DC2626" }} />}
                    {control?.ucl_2s != null && <ReferenceLine y={control.ucl_2s} stroke="#F59E0B" strokeDasharray="2 4" label={{ value: "+2σ", position: "right", fontSize: 9, fill: "#F59E0B" }} />}
                    {control?.lcl_2s != null && <ReferenceLine y={control.lcl_2s} stroke="#F59E0B" strokeDasharray="2 4" label={{ value: "−2σ", position: "right", fontSize: 9, fill: "#F59E0B" }} />}
                    {control?.cl != null  && <ReferenceLine y={control.cl}  stroke="#059669" strokeDasharray="6 3" label={{ value: "CL",  position: "right", fontSize: 10, fill: "#059669" }} />}
                    {median != null && <ReferenceLine y={median} stroke="#EA580C" strokeWidth={1.5} label={{ value: `Median ${median}`, position: "right", fontSize: 10, fill: "#EA580C" }} />}
                    <Line
                        type="monotone"
                        dataKey="v"
                        stroke="#0F1E37"
                        strokeWidth={2}
                        isAnimationActive={false}
                        dot={RunChartDot}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

function RunsTestPanel({ runs }) {
    if (!runs || runs.insufficient_data) {
        return (
            <div className="mt-2 text-[10px] text-slate-500 italic">
                Nonparametric runs test needs at least 6 data points.
            </div>
        );
    }
    const flag = (p) => (p != null && p < 0.05) ? "text-rose-600 font-black" : "text-slate-700";
    const pFmt = (p) => (p == null ? "—" : p < 0.001 ? "<0.001" : p);
    return (
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-2" data-testid="spc-runs-test">
            <div className="uppercase-label text-[9px] font-bold mb-1 text-slate-600">Nonparametric Runs Test</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5 text-[11px]">
                <MiniRow label="Number of Runs about Median"          value={runs.runs_about_median} />
                <MiniRow label="Expected Number of Runs about Median" value={runs.expected_runs_median} />
                <MiniRow label="Number of Points above Median"        value={runs.points_above_median} />
                <MiniRow label="Number of Points equal to or below Median" value={runs.points_below_median} />
                <MiniRow label="P-Value for Clustering"         value={pFmt(runs.p_clustering)}   valueClass={flag(runs.p_clustering)} />
                <MiniRow label="P-Value for Mixtures"           value={pFmt(runs.p_mixtures)}     valueClass={flag(runs.p_mixtures)} />
                <MiniRow label="P-Value for Trends"             value={pFmt(runs.p_trends)}       valueClass={flag(runs.p_trends)} />
                <MiniRow label="P-Value for Oscillations"       value={pFmt(runs.p_oscillations)} valueClass={flag(runs.p_oscillations)} />
            </div>
            <div className="text-[9.5px] text-slate-500 mt-1">
                <b>Clustering</b> — few runs (shifts). <b>Mixtures</b> — many runs (alternating).
                <b> Trends</b> — few runs up/down (drift). <b>Oscillations</b> — many runs up/down (sawtooth).
                Red p-values (&lt; 0.05) indicate a detected pattern.
            </div>
        </div>
    );
}

function MiniRow({ label, value, valueClass = "" }) {
    return (
        <>
            <div className="text-slate-600">{label}</div>
            <div className={`text-right tabular ${valueClass}`}>{value ?? "—"}</div>
        </>
    );
}

function QqChart({ data, unit }) {
    // SigmaXL convention: X = sample value, Y = NSCORE (theoretical Z).
    // Reference line + upper/lower 95% confidence bands.
    const pts = (data.points || []).map((p) => ({ x: p.sample, y: p.nscore }));
    const ref = (data.ref || []).map((p) => ({ x: p.sample, y: p.nscore }));
    const bandU = (data.band_upper || []).map((p) => ({ x: p.sample, y: p.nscore }));
    const bandL = (data.band_lower || []).map((p) => ({ x: p.sample, y: p.nscore }));
    if (!pts.length) return null;
    return (
        <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 8, right: 10, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#EEF1F5" />
                    <XAxis type="number" dataKey="x" stroke="#64748B" fontSize={10} label={{ value: unit || "sample", position: "insideBottom", offset: -2, fontSize: 10, fill: "#64748B" }} />
                    <YAxis type="number" dataKey="y" stroke="#64748B" fontSize={10} label={{ value: "NSCORE", angle: -90, position: "insideLeft", fontSize: 10, fill: "#64748B" }} />
                    <Tooltip
                        contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1", fontSize: 11 }}
                        formatter={(v, k) => [v, k === "x" ? "sample" : "z"]}
                    />
                    {/* CI bands (drawn as line-only scatter series) */}
                    {bandU.length > 0 && (
                        <Scatter name="upper 95%" data={bandU} line={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "3 3" }} shape={() => null} isAnimationActive={false} />
                    )}
                    {bandL.length > 0 && (
                        <Scatter name="lower 95%" data={bandL} line={{ stroke: "#94A3B8", strokeWidth: 1, strokeDasharray: "3 3" }} shape={() => null} isAnimationActive={false} />
                    )}
                    {/* Reference line */}
                    {ref.length === 2 && (
                        <Scatter name="reference" data={ref} line={{ stroke: "#DC2626", strokeWidth: 1.5 }} shape={() => null} isAnimationActive={false} />
                    )}
                    {/* Sample points */}
                    <Scatter name="sample" data={pts} fill="#1D4ED8" isAnimationActive={false} />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}

function BoxChart({ box, unit }) {
    // Vertical box plot — value on Y-axis (top = max, bottom = min).
    // Outliers rendered as small circles at their true Y position, gently
    // jittered on X for readability.
    const W = 360, H = 420;
    const PAD_TOP = 24, PAD_BOTTOM = 40, PAD_LEFT = 60, PAD_RIGHT = 60;
    const plotH = H - PAD_TOP - PAD_BOTTOM;
    const outliers = (box.outliers || []).filter((v) => v != null);
    const values = [
        box.lower_fence ?? box.min, box.min, box.q1, box.median, box.q3, box.max,
        box.upper_fence ?? box.max, ...outliers,
    ].filter((v) => v != null);
    if (!values.length) return null;

    // Add ~5% headroom above/below so outliers don't sit exactly on the edge.
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const rawSpan = rawMax === rawMin ? Math.abs(rawMax) || 1 : rawMax - rawMin;
    const pad = rawSpan * 0.05;
    const vMin = rawMin - pad;
    const vMax = rawMax + pad;
    const range = vMax - vMin || 1;
    // Y grows downward in SVG, so invert to place max at the top.
    const yScale = (v) => PAD_TOP + (1 - (v - vMin) / range) * plotH;

    const cx = PAD_LEFT + (W - PAD_LEFT - PAD_RIGHT) / 2;      // vertical centre of the box
    const boxHalf = 46;                                         // half-width of the IQR box
    const capHalf = 26;                                         // half-width of whisker caps

    // Number formatter that adapts to the data range.
    const fmt = (v) => {
        const abs = Math.abs(v);
        if (abs === 0) return "0";
        if (abs >= 1000) return v.toFixed(0);
        if (abs >= 10) return v.toFixed(1);
        return v.toFixed(2);
    };

    // 6 evenly-spaced Y-axis ticks.
    const ticks = [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => vMin + f * range);

    // Deterministic pseudo-random jitter for outlier X — keeps layout stable.
    const jitter = (i) => {
        const s = Math.sin(i * 12.9898) * 43758.5453;
        return (s - Math.floor(s) - 0.5) * (boxHalf * 0.9);
    };

    return (
        <div className="overflow-x-auto">
            <svg width={W} height={H} className="mx-auto block" role="img" aria-label="Vertical box plot">
                {/* Y-axis line */}
                <line x1={PAD_LEFT} x2={PAD_LEFT} y1={PAD_TOP} y2={H - PAD_BOTTOM} stroke="#CBD5E1" />
                {/* Y ticks + gridlines + labels */}
                {ticks.map((v, i) => {
                    const y = yScale(v);
                    return (
                        <g key={i}>
                            <line x1={PAD_LEFT} x2={W - PAD_RIGHT + 10} y1={y} y2={y} stroke="#E2E8F0" strokeDasharray="2 3" />
                            <line x1={PAD_LEFT - 4} x2={PAD_LEFT} y1={y} y2={y} stroke="#64748B" />
                            <text x={PAD_LEFT - 8} y={y + 3} fontSize={10} fill="#64748B" textAnchor="end">{fmt(v)}</text>
                        </g>
                    );
                })}

                {/* Lower whisker (Q1 -> min) */}
                <line x1={cx} x2={cx} y1={yScale(box.q1)} y2={yScale(box.min)} stroke="#0F1E37" strokeWidth={1.5} />
                {/* Upper whisker (Q3 -> max) */}
                <line x1={cx} x2={cx} y1={yScale(box.q3)} y2={yScale(box.max)} stroke="#0F1E37" strokeWidth={1.5} />
                {/* Whisker caps */}
                <line x1={cx - capHalf} x2={cx + capHalf} y1={yScale(box.min)} y2={yScale(box.min)} stroke="#0F1E37" strokeWidth={1.5} />
                <line x1={cx - capHalf} x2={cx + capHalf} y1={yScale(box.max)} y2={yScale(box.max)} stroke="#0F1E37" strokeWidth={1.5} />

                {/* IQR box */}
                <rect
                    x={cx - boxHalf}
                    y={yScale(box.q3)}
                    width={boxHalf * 2}
                    height={Math.max(1, yScale(box.q1) - yScale(box.q3))}
                    fill="#3B82F6" fillOpacity={0.18} stroke="#1D4ED8" strokeWidth={1.5}
                />
                {/* Median line */}
                <line
                    x1={cx - boxHalf} x2={cx + boxHalf}
                    y1={yScale(box.median)} y2={yScale(box.median)}
                    stroke="#DC2626" strokeWidth={2.5}
                />

                {/* Q1 / Median / Q3 side labels */}
                <text x={cx + boxHalf + 6} y={yScale(box.q3) + 3} fontSize={10} fill="#1D4ED8">Q3 {fmt(box.q3)}</text>
                <text x={cx + boxHalf + 6} y={yScale(box.median) + 3} fontSize={10} fill="#DC2626" fontWeight={600}>Med {fmt(box.median)}</text>
                <text x={cx + boxHalf + 6} y={yScale(box.q1) + 3} fontSize={10} fill="#1D4ED8">Q1 {fmt(box.q1)}</text>
                <text x={cx + capHalf + 6} y={yScale(box.max) + 3} fontSize={10} fill="#64748B">Max {fmt(box.max)}</text>
                <text x={cx + capHalf + 6} y={yScale(box.min) + 3} fontSize={10} fill="#64748B">Min {fmt(box.min)}</text>

                {/* Outliers — small circles at true value, jittered horizontally */}
                {outliers.map((o, i) => (
                    <g key={i}>
                        <circle
                            cx={cx + jitter(i)}
                            cy={yScale(o)}
                            r={3.5}
                            fill="#FFFFFF"
                            stroke="#DC2626"
                            strokeWidth={1.5}
                        >
                            <title>{`Outlier: ${fmt(o)} ${unit || ""}`}</title>
                        </circle>
                        <text
                            x={cx - boxHalf - 6}
                            y={yScale(o) + 3}
                            fontSize={9}
                            fill="#B91C1C"
                            textAnchor="end"
                        >
                            {fmt(o)}
                        </text>
                    </g>
                ))}

                {/* Y-axis title (rotated) */}
                <text
                    x={16} y={PAD_TOP + plotH / 2}
                    fontSize={10} fill="#64748B" textAnchor="middle"
                    transform={`rotate(-90 16 ${PAD_TOP + plotH / 2})`}
                >
                    {unit || "value"}
                </text>

                {/* X-axis category label */}
                <text x={cx} y={H - 12} fontSize={10} fill="#64748B" textAnchor="middle">
                    n = {(box.n ?? "").toString() || ""}{outliers.length > 0 ? ` · ${outliers.length} outlier${outliers.length === 1 ? "" : "s"}` : ""}
                </text>
            </svg>
        </div>
    );
}

function HistogramChart({ hist, stats, cap, unit }) {
    // Combined bar + overlay line by rendering into a Recharts BarChart with a
    // secondary XAxis for the overlay. Simpler: convert overlay to bar data
    // interpolated to bin centres.
    const overlayMap = new Map();
    (hist.normal_overlay || []).forEach((p) => overlayMap.set(p.x, p.y));
    const rows = (hist.bins || []).map((b) => {
        // find nearest overlay y
        let bestX = null, bestY = null, bestD = Infinity;
        for (const [ox, oy] of overlayMap) {
            const d = Math.abs(ox - b.x);
            if (d < bestD) { bestD = d; bestX = ox; bestY = oy; }
        }
        return { x: b.x, count: b.count, overlay: bestY, x_low: b.x_low, x_high: b.x_high };
    });

    return (
        <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 10, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#EEF1F5" />
                    <XAxis dataKey="x" stroke="#64748B" fontSize={10} label={{ value: unit || "value", position: "insideBottom", offset: -2, fontSize: 10, fill: "#64748B" }} />
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip
                        contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1", fontSize: 11 }}
                        formatter={(v, key) => key === "count" ? [v, "count"] : [v, "N(μ,σ)"]}
                        labelFormatter={(v, payload) => (payload && payload[0]) ? `${payload[0].payload.x_low} – ${payload[0].payload.x_high}` : v}
                    />
                    <Bar dataKey="count" fill="#0F1E37">
                        {rows.map((r, i) => (
                            <Cell key={i} fill={
                                (stats.mean != null && r.x_low <= stats.mean && r.x_high >= stats.mean) ? "#DC2626" : "#0F1E37"
                            } />
                        ))}
                    </Bar>
                    <Line type="monotone" dataKey="overlay" stroke="#F59E0B" strokeWidth={2} dot={false} />
                    {stats.mean != null && <ReferenceLine x={stats.mean} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "μ", position: "top", fontSize: 10, fill: "#DC2626" }} />}
                    {cap?.lsl != null && !cap.auto_spec && <ReferenceLine x={cap.lsl} stroke="#7C3AED" strokeWidth={1.5} label={{ value: "LSL", position: "top", fontSize: 10, fill: "#7C3AED" }} />}
                    {cap?.usl != null && !cap.auto_spec && <ReferenceLine x={cap.usl} stroke="#7C3AED" strokeWidth={1.5} label={{ value: "USL", position: "top", fontSize: 10, fill: "#7C3AED" }} />}
                    {cap?.target != null && <ReferenceLine x={cap.target} stroke="#059669" strokeWidth={1.5} label={{ value: "T", position: "top", fontSize: 10, fill: "#059669" }} />}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

/* ─── helpers ─────────────────────────────────────────────────────────── */
function pct(v) {
    if (v == null) return "—";
    return `${v}%`;
}
function fmtP(v) {
    // Show tiny p-values as "p<0.001" rather than "p=0" which is misleading.
    if (v == null) return "p=—";
    if (v < 0.001) return "p<0.001";
    if (v < 0.01)  return `p=${v.toFixed(3)}`;
    return `p=${v}`;
}
function fmtHours(v) {
    if (v == null) return "—";
    if (v < 1) return `${Math.round(v * 60)} min`;
    if (v < 48) return `${v.toFixed(1)} h`;
    const d = v / 24;
    return `${d.toFixed(1)} d`;
}
function fmtBucket(h) {
    if (h == null) return "—";
    if (h === 1)   return "hourly";
    if (h === 12)  return "12h shift";
    if (h === 24)  return "daily";
    if (h === 168) return "weekly";
    if (h < 24)    return `${h}h`;
    return `${(h / 24).toFixed(0)}d`;
}
function toneForPct(v) {
    if (v == null) return "text-slate-700";
    if (v >= 90) return "text-emerald-700";
    if (v >= 75) return "text-amber-700";
    return "text-rose-700";
}
function toneForSigma(v) {
    if (v == null) return "text-slate-700";
    if (v >= 6)   return "text-emerald-700";
    if (v >= 4.5) return "text-emerald-600";
    if (v >= 3)   return "text-amber-700";
    return "text-rose-700";
}
function toneForCpk(v) {
    if (v == null) return "bg-slate-100 text-slate-700 border-slate-200";
    if (v >= 1.67) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (v >= 1.33) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (v >= 1.0)  return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-rose-100 text-rose-700 border-rose-200";
}

function CapabilityStrip({ cap, unit }) {
    const items = [
        { label: "Cp",  value: cap.cp,  hint: "Potential capability" },
        { label: "Cpk", value: cap.cpk, hint: "Actual capability (accounts for centering)" },
        { label: "Pp",  value: cap.pp,  hint: "Performance (long-term)" },
        { label: "Ppk", value: cap.ppk, hint: "Performance actual (long-term + centering)" },
        { label: "Sigma level (Z_ST + 1.5)", value: cap.sigma_level, hint: "Six-Sigma scale" },
        { label: "DPMO", value: cap.dpmo != null ? Math.round(cap.dpmo).toLocaleString() : null, hint: "Defects per million opportunities" },
        { label: "Observed defect %", value: cap.observed_defect_pct != null ? `${cap.observed_defect_pct}%` : null, hint: "Empirical out-of-spec rate" },
    ];
    return (
        <div className="rounded border border-slate-200 bg-white" data-testid="spc-capability-strip">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-1">
                <Target size={13} className="text-slate-500" />
                <div className="uppercase-label text-[10px] font-bold">
                    Process capability{cap.auto_spec ? " (auto ±3σ spec)" : ""}
                    {" · "}
                    <span className="text-slate-500 normal-case">
                        {cap.lsl != null && `LSL ${cap.lsl}`}
                        {cap.target != null && ` · T ${cap.target}`}
                        {cap.usl != null && ` · USL ${cap.usl}`}
                        {" "}<span className="uppercase">{unit}</span>
                    </span>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 p-3">
                {items.map((it) => (
                    <div key={it.label} className="text-center">
                        <div className="text-[9px] uppercase text-slate-500 font-bold" title={it.hint}>{it.label}</div>
                        <div className="tabular text-[15px] font-black text-[var(--grindrod-navy)]">{it.value ?? "—"}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const SEVERITY_META = {
    high:     { label: "HIGH",     tone: "border-rose-200 bg-rose-50 text-rose-700",         icon: WarningCircle, dot: "bg-rose-500" },
    medium:   { label: "MEDIUM",   tone: "border-amber-200 bg-amber-50 text-amber-700",      icon: Warning,       dot: "bg-amber-500" },
    positive: { label: "GOOD",     tone: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle,   dot: "bg-emerald-500" },
    low:      { label: "LOW",      tone: "border-slate-200 bg-slate-50 text-slate-700",      icon: Lightbulb,     dot: "bg-slate-400" },
};

const DMAIC_META = {
    Define:   "bg-blue-100 text-blue-700 border-blue-200",
    Measure:  "bg-violet-100 text-violet-700 border-violet-200",
    Analyze:  "bg-amber-100 text-amber-700 border-amber-200",
    Improve:  "bg-teal-100 text-teal-700 border-teal-200",
    Control:  "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function FindingsPanel({ findings }) {
    if (!findings || findings.length === 0) return null;
    const counts = { high: 0, medium: 0, low: 0, positive: 0 };
    findings.forEach((f) => { counts[f.severity] = (counts[f.severity] || 0) + 1; });
    return (
        <div className="rounded border border-slate-200 bg-white" data-testid="spc-findings">
            <div className="px-4 py-3 bg-[var(--grindrod-navy)] text-white flex items-center gap-2">
                <Lightbulb size={18} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide text-sm">
                    Six-Sigma auto-diagnosis · findings &amp; recommendations
                </div>
                <div className="ml-auto flex items-center gap-1 text-[10px]">
                    <SummaryBadge n={counts.high}     tone="bg-rose-500"    label="HIGH" />
                    <SummaryBadge n={counts.medium}   tone="bg-amber-500"   label="MED" />
                    <SummaryBadge n={counts.low}      tone="bg-slate-400"   label="LOW" />
                    <SummaryBadge n={counts.positive} tone="bg-emerald-500" label="GOOD" />
                </div>
            </div>
            <div className="p-3 space-y-2">
                {findings.map((f, i) => <FindingCard key={i} f={f} idx={i} />)}
            </div>
        </div>
    );
}

function SummaryBadge({ n, tone, label }) {
    return (
        <span className={`${tone} text-white font-bold px-1.5 py-0.5 rounded text-[9px] tracking-wider`}>
            {n} {label}
        </span>
    );
}

function FindingCard({ f, idx }) {
    const meta = SEVERITY_META[f.severity] || SEVERITY_META.low;
    const Icon = meta.icon;
    const dmaic = DMAIC_META[f.dmaic] || "bg-slate-100 text-slate-700 border-slate-200";
    return (
        <div className={`rounded border-l-4 ${meta.tone.replace("bg-", "border-l-").split(" ")[0]} border ${meta.tone} p-3`} data-testid={`spc-finding-${idx}`}>
            <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${meta.dot} rounded-full w-2 h-2 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[9px] font-black tracking-wider ${dmaic}`}>{f.dmaic}</span>
                        <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{f.category}</span>
                        <span className={`inline-flex items-center gap-1 text-[9px] font-black tracking-wider ${meta.tone.split(' ').filter(c => c.startsWith('text-'))[0]}`}>
                            <Icon size={11} weight="fill" /> {meta.label}
                        </span>
                    </div>
                    <div className="font-display font-black text-sm text-slate-900 mt-1">{f.title}</div>
                    <div className="text-xs text-slate-700 mt-0.5">{f.finding}</div>
                    <div className="text-xs text-slate-800 mt-1 flex items-start gap-1">
                        <Lightbulb size={12} className="text-amber-600 mt-0.5 flex-shrink-0" weight="fill" />
                        <span><b className="text-slate-900">Recommendation:</b> {f.recommendation}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
